// campaign-recurrence-summary.ts — a human-readable cadence for a campaign's
// rruleset ("every week on Tuesday, 3 times in total"), for the wizard's Timing
// and Review steps and the launched screen.
//
// It used to return rrule's own `toText()`. Two things were wrong with that, and
// both were visible on screen:
//
//   1. `toText()` is ENGLISH ONLY. A Hebrew user reading a Hebrew campaign
//      summary was told "every week on Tuesday for 3 times", in Latin script,
//      left-to-right, in the middle of their sentence.
//   2. It is a description of a RULE, not of a campaign. "for 3 times" is the
//      shape of an RFC 5545 COUNT — it is not a phrase anybody says.
//
// The cadence itself now comes from `buildRecurrenceSentence`, the calendar's
// localized sentence, so the campaign screens and the planner describe the same
// schedule with the same words instead of each inventing their own. What that
// helper deliberately leaves out is where the series ENDS, which a person about
// to launch a campaign very much wants to know — so the ending is added here,
// through locale keys, as its own clause.

import { buildRecurrenceSentence } from '@common/views/calendar/grid/recurrence-sentence';
import { fetchRule } from '@common-components/schedule-picker/recurrence-utils';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const KEY = 'campaignsRedesign.recurrenceEnd.';

/**
 * `t` is required: a summary with no translator is a summary in the wrong
 * language, and that was the bug.
 *
 * Never throws — an unparseable rule yields '', and every caller already omits
 * the line when it is empty.
 */
export function recurrenceSummary(
  rruleset: string | undefined | null,
  t: Translate,
  formatDate?: (date: Date) => string,
): string {
  if (!rruleset) return '';

  const cadence = buildRecurrenceSentence(rruleset, t);
  if (!cadence) return '';

  try {
    const { count, until } = fetchRule(rruleset).options;

    // "3 times in total" — a number of sends, which is what the person choosing
    // it was counting. Not "for 3 times".
    if (typeof count === 'number' && count > 0) {
      return t(`${KEY}afterCount`, { count, cadence });
    }

    if (until instanceof Date && !isNaN(until.getTime())) {
      const date = formatDate
        ? formatDate(until)
        : until.toLocaleDateString(undefined, { dateStyle: 'medium' } as any);
      return t(`${KEY}untilDate`, { cadence, date });
    }
  } catch {
    // A rule we cannot read the ending of still has a readable cadence.
  }

  return cadence;
}
